import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Tag,
  Button,
  Space,
  Tabs,
  Table,
  Statistic,
  Timeline,
  Progress,
  List,
  Descriptions,
  Badge,
  Divider,
  Input,
  Select,
  DatePicker,
  Form,
  Modal,
  Drawer,
  Alert
} from 'antd';
import {
  UserOutlined,
  BuildOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  CreditCardOutlined,
  ToolOutlined,
  ProjectOutlined,
  SettingOutlined,
  EditOutlined,
  PlusOutlined,
  EyeOutlined,
  DownloadOutlined,
  MessageOutlined,
  BellOutlined,
  StarFilled,
  StarOutlined,
  TrophyOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

interface Customer {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'active' | 'inactive' | 'prospect' | 'vip';
  type: 'individual' | 'business' | 'enterprise';
  assignedTo: string;
  createdAt: string;
  lastContact: string;
  totalValue: number;
  annualBilling: number;
  totalUnpaid: number;
  avatar?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  tags: string[];
  notes: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'order' | 'payment' | 'support' | 'note';
  title: string;
  description: string;
  date: string;
  user: string;
  status?: 'completed' | 'pending' | 'cancelled';
}

interface Order {
  id: string;
  orderNo: string;
  date: string;
  amount: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  items: number;
}

interface Payment {
  id: string;
  invoiceNo: string;
  date: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'partial';
  dueDate: string;
}

// Sample customer data
const sampleCustomer: Customer = {
  id: '1',
  name: 'Hong Kong Biotechnology Organization',
  company: 'Hong Kong Biotechnology Organization',
  email: 'contact@hkbiotech.org',
  phone: '+852 2234 5678',
  status: 'active',
  type: 'enterprise',
  assignedTo: 'Sarah Johnson',
  createdAt: '2023-01-15',
  lastContact: '2024-06-20',
  totalValue: 500000,
  annualBilling: 100000,
  totalUnpaid: 50000,
  address: {
    street: '123 Science Park Road',
    city: 'Hong Kong',
    state: 'Hong Kong',
    zipCode: '999077',
    country: 'Hong Kong SAR'
  },
  tags: ['Enterprise', 'Biotechnology', 'Research', 'VIP'],
  notes: 'Key enterprise client in biotechnology sector. Requires specialized support and custom solutions.',
  priority: 'high'
};

const sampleActivities: Activity[] = [
  {
    id: '1',
    type: 'meeting',
    title: 'Quarterly Business Review',
    description: 'Discussed expansion plans and new module requirements',
    date: '2024-06-20',
    user: 'Sarah Johnson',
    status: 'completed'
  },
  {
    id: '2',
    type: 'order',
    title: 'New Order Placed',
    description: 'Order #SO-2024-156 for additional licenses',
    date: '2024-06-18',
    user: 'System',
    status: 'confirmed'
  },
  {
    id: '3',
    type: 'payment',
    title: 'Payment Received',
    description: 'Invoice #INV-2024-089 paid - HK$25,000',
    date: '2024-06-15',
    user: 'System',
    status: 'completed'
  }
];

const sampleOrders: Order[] = [
  {
    id: '1',
    orderNo: 'SO-2024-156',
    date: '2024-06-18',
    amount: 75000,
    status: 'confirmed',
    items: 15
  },
  {
    id: '2',
    orderNo: 'SO-2024-134',
    date: '2024-05-22',
    amount: 45000,
    status: 'delivered',
    items: 8
  }
];

const samplePayments: Payment[] = [
  {
    id: '1',
    invoiceNo: 'INV-2024-089',
    date: '2024-06-15',
    amount: 25000,
    status: 'paid',
    dueDate: '2024-06-30'
  },
  {
    id: '2',
    invoiceNo: 'INV-2024-092',
    date: '2024-06-20',
    amount: 50000,
    status: 'pending',
    dueDate: '2024-07-20'
  }
];

interface CustomerProfileProps {
  customerId?: string;
  onClose?: () => void;
}

const CustomerProfile: React.FC<CustomerProfileProps> = ({ customerId, onClose }) => {
  const [customer] = useState<Customer>(sampleCustomer);
  const [activities] = useState<Activity[]>(sampleActivities);
  const [orders] = useState<Order[]>(sampleOrders);
  const [payments] = useState<Payment[]>(samplePayments);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const [form] = Form.useForm();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'vip': return 'gold';
      case 'prospect': return 'blue';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call': return <PhoneOutlined style={{ color: '#1890ff' }} />;
      case 'email': return <MailOutlined style={{ color: '#52c41a' }} />;
      case 'meeting': return <CalendarOutlined style={{ color: '#fa8c16' }} />;
      case 'order': return <ShoppingCartOutlined style={{ color: '#722ed1' }} />;
      case 'payment': return <CreditCardOutlined style={{ color: '#13c2c2' }} />;
      case 'support': return <ToolOutlined style={{ color: '#eb2f96' }} />;
      default: return <FileTextOutlined />;
    }
  };

  const orderColumns = [
    {
      title: 'Order No',
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => <Text strong>HK${amount.toLocaleString()}</Text>
    },
    {
      title: 'Items',
      dataIndex: 'items',
      key: 'items'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'delivered' ? 'green' : status === 'confirmed' ? 'blue' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} size="small" />
          <Button type="text" icon={<DownloadOutlined />} size="small" />
        </Space>
      )
    }
  ];

  const paymentColumns = [
    {
      title: 'Invoice No',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => <Text strong>HK${amount.toLocaleString()}</Text>
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'paid' ? 'green' : status === 'pending' ? 'orange' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar 
                size={80} 
                style={{ backgroundColor: '#1890ff', fontSize: '24px' }}
                icon={<BuildOutlined />}
              >
                HK
              </Avatar>
              <div>
                <Title level={2} style={{ margin: 0 }}>
                  {customer.name}
                </Title>
                <Space>
                  <Tag color={getStatusColor(customer.status)}>
                    {customer.status.toUpperCase()}
                  </Tag>
                  <Tag color={getPriorityColor(customer.priority)}>
                    {customer.priority.toUpperCase()} PRIORITY
                  </Tag>
                  <Tag color="blue">{customer.type.toUpperCase()}</Tag>
                </Space>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    <MailOutlined /> {customer.email} | <PhoneOutlined /> {customer.phone}
                  </Text>
                </div>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<MessageOutlined />}>New Email</Button>
              <Button icon={<CalendarOutlined />}>New Event</Button>
              <Button type="primary" icon={<EditOutlined />} onClick={() => setIsEditModalVisible(true)}>
                Edit
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Annual Billing"
              value={customer.annualBilling}
              prefix="HK$"
              valueStyle={{ color: '#3f8600' }}
              suffix={
                <span style={{ fontSize: '14px', color: '#52c41a' }}>
                  <RiseOutlined /> 12.5%
                </span>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Unpaid"
              value={customer.totalUnpaid}
              prefix="HK$"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={customer.totalValue}
              prefix="HK$"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Days Since Last Contact"
              value={Math.floor((new Date().getTime() - new Date(customer.lastContact).getTime()) / (1000 * 3600 * 24))}
              suffix="days"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <TabPane tab="Dashboard" key="dashboard">
            <Row gutter={24}>
              <Col span={16}>
                {/* Activity Timeline */}
                <Card title="Recent Activity" style={{ marginBottom: 24 }}>
                  <Timeline>
                    {activities.map(activity => (
                      <Timeline.Item key={activity.id} dot={getActivityIcon(activity.type)}>
                        <div>
                          <Text strong>{activity.title}</Text>
                          <br />
                          <Text type="secondary">{activity.description}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {new Date(activity.date).toLocaleDateString()} by {activity.user}
                          </Text>
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </Card>

                {/* Recent Orders */}
                <Card title="Recent Orders">
                  <Table 
                    columns={orderColumns} 
                    dataSource={orders} 
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
              <Col span={8}>
                {/* Customer Info */}
                <Card title="Customer Information" style={{ marginBottom: 24 }}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Company">{customer.company}</Descriptions.Item>
                    <Descriptions.Item label="Email">{customer.email}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{customer.phone}</Descriptions.Item>
                    <Descriptions.Item label="Assigned To">{customer.assignedTo}</Descriptions.Item>
                    <Descriptions.Item label="Customer Since">
                      {new Date(customer.createdAt).toLocaleDateString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Address">
                      {customer.address.street}<br />
                      {customer.address.city}, {customer.address.state}<br />
                      {customer.address.zipCode}, {customer.address.country}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Tags */}
                <Card title="Tags" style={{ marginBottom: 24 }}>
                  <Space wrap>
                    {customer.tags.map(tag => (
                      <Tag key={tag} color="blue">{tag}</Tag>
                    ))}
                    <Button type="dashed" size="small" icon={<PlusOutlined />}>
                      Add Tag
                    </Button>
                  </Space>
                </Card>

                {/* Notes */}
                <Card title="Notes">
                  <Paragraph>{customer.notes}</Paragraph>
                  <Button type="link" onClick={() => setIsCommentModalVisible(true)}>
                    Add Comment
                  </Button>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Contact & Address" key="contact">
            <Card>
              <Descriptions title="Contact Information" bordered column={2}>
                <Descriptions.Item label="Full Name">{customer.name}</Descriptions.Item>
                <Descriptions.Item label="Company">{customer.company}</Descriptions.Item>
                <Descriptions.Item label="Email">{customer.email}</Descriptions.Item>
                <Descriptions.Item label="Phone">{customer.phone}</Descriptions.Item>
                <Descriptions.Item label="Type">{customer.type}</Descriptions.Item>
                <Descriptions.Item label="Status">{customer.status}</Descriptions.Item>
                <Descriptions.Item label="Address" span={2}>
                  {customer.address.street}<br />
                  {customer.address.city}, {customer.address.state} {customer.address.zipCode}<br />
                  {customer.address.country}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>

          <TabPane tab="Orders" key="orders">
            <Card>
              <Table 
                columns={orderColumns} 
                dataSource={orders} 
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </TabPane>

          <TabPane tab="Payments" key="payments">
            <Card>
              <Table 
                columns={paymentColumns} 
                dataSource={payments} 
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </TabPane>

          <TabPane tab="Support" key="support">
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <ToolOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                <Title level={4} style={{ marginTop: '16px', color: '#8c8c8c' }}>
                  Support Tickets
                </Title>
                <Text type="secondary">
                  Support ticket management coming soon
                </Text>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Projects" key="projects">
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <ProjectOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                <Title level={4} style={{ marginTop: '16px', color: '#8c8c8c' }}>
                  Projects
                </Title>
                <Text type="secondary">
                  Project management coming soon
                </Text>
              </div>
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* Edit Customer Modal */}
      <Modal
        title="Edit Customer"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={() => {
          form.validateFields().then(() => {
            setIsEditModalVisible(false);
          });
        }}
        width={600}
      >
        <Form form={form} layout="vertical" initialValues={customer}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Customer Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company" label="Company">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="prospect">Prospect</Option>
                  <Option value="vip">VIP</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="type" label="Type">
                <Select>
                  <Option value="individual">Individual</Option>
                  <Option value="business">Business</Option>
                  <Option value="enterprise">Enterprise</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Priority">
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="critical">Critical</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Comment Modal */}
      <Modal
        title="Add Comment"
        open={isCommentModalVisible}
        onCancel={() => setIsCommentModalVisible(false)}
        onOk={() => setIsCommentModalVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item name="comment" label="Comment">
            <TextArea rows={4} placeholder="Enter your comment..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerProfile; 